// Free, rule-based "brain" for the TripTrix chat assistant (no LLM / no API cost).
// Matches a visitor's free-text question to an intent and returns a canned,
// on-brand reply. Strictly B2B: never quotes retail prices ("net rates on request").
import { SITE } from "@/lib/site";
import { getRelatedGuides } from "@/lib/relatedGuides";

const norm = (s) => ` ${String(s || "").toLowerCase().replace(/[^a-z0-9\s]/g, " ").replace(/\s+/g, " ").trim()} `;
const hasAny = (t, arr) => arr.some((w) => t.includes(` ${w} `) || t.includes(`${w} `) || t.includes(` ${w}`));

// Ordered: domain intents are checked before small talk so "hi, best time to visit?"
// resolves to the useful answer, while a bare "hi" falls through to the greeting.
const INTENTS = [
  {
    id: "netrates",
    words: ["price", "prices", "cost", "costs", "rate", "rates", "net rate", "net rates", "quote", "quotation", "charge", "charges", "how much", "budget", "fee", "fees", "pricing", "tariff"],
    reply: {
      text: "We share confidential net rates for the travel trade (no public/retail pricing). Send your dates, pax and destinations and our desk comes back within 24 hours.",
      links: [{ label: "Send an enquiry", to: "/contact" }],
      wa: "Hi TripTrix, I'd like net rates. My dates / pax / destination are:",
      openLead: true,
    },
  },
  {
    id: "permits",
    words: ["permit", "permits", "ilp", "pap", "inner line", "protected area", "restricted area", "restricted areas", "visa"],
    reply: {
      text: "We handle the paperwork for restricted areas: Inner Line Permits (ILP) for Arunachal, Nagaland, Mizoram & Manipur, and Protected Area Permits (PAP) for parts of Sikkim. Bhutan entry is arranged too. Just share nationality and route.",
      links: [{ label: "Read our guides", to: "/journal" }],
      wa: "Hi TripTrix, I have a question about permits (ILP/PAP):",
    },
  },
  {
    id: "transport",
    words: ["car", "cab", "cabs", "taxi", "taxis", "vehicle", "vehicles", "suv", "innova", "xylo", "tempo", "traveller", "sedan", "driver", "pickup", "pick up", "airport transfer", "transfer", "transfers", "fleet"],
    reply: {
      text: "We run SUVs (Innova/Xylo), tempo travellers and sedans with drivers and permits across the region, plus airport/railway transfers (Bagdogra, Guwahati, NJP). Tell us the route and dates for net rates.",
      links: [{ label: "Airport & rail transfers", to: "/bagdogra-airport-transfers" }],
      wa: "Hi TripTrix, I need vehicle/transport — please share net rates for:",
    },
  },
  {
    id: "besttime",
    words: ["best time", "when to visit", "when to go", "weather", "season", "seasons", "month", "months", "climate", "snow", "monsoon", "rain"],
    reply: {
      text: "Oct–Apr is great region-wide; Mar–May for blooms and clear mountain views; Dec–Mar for snow in Sikkim and Tawang; and the monsoon is spectacular in Meghalaya. Which destination are you planning?",
      links: [{ label: "Journal & guides", to: "/journal" }],
    },
  },
  {
    id: "packages",
    words: ["package", "packages", "tour", "tours", "itinerary", "itineraries", "trip", "trips", "holiday", "holidays", "circuit", "programme", "program"],
    reply: {
      text: "We plan North East, Sikkim, Darjeeling, Bhutan and Nepal itineraries — ready to sell or fully tailored to your client, on B2B net rates only.",
      links: [
        { label: "All tour packages", to: "/tours" },
        { label: "Explore destinations", to: "/destinations" },
      ],
      wa: "Hi TripTrix, I'd like net rates for a tour package:",
    },
  },
  {
    id: "booking",
    words: ["book", "booking", "how to book", "agent", "agents", "b2b", "partner", "partners", "work with", "become an agent", "register", "sign up", "commission", "white label", "white-label", "reseller"],
    reply: {
      text: "We're a B2B DMC — we work with travel agents and tour operators, not directly with travellers. Send your client's brief and we return net rates plus a day-wise itinerary, white-labelled to your brand.",
      links: [
        { label: "Why agents trust us", to: "/partners" },
        { label: "Send an enquiry", to: "/contact" },
      ],
      wa: "Hi TripTrix, I'm a travel agent — I'd like to work with you.",
    },
  },
  {
    id: "contact",
    words: ["contact", "phone", "call", "email", "mail", "office", "offices", "address", "reach", "location", "whatsapp", "siliguri", "guwahati"],
    reply: {
      text: `Reach our trade desk on ${SITE.phoneDisplay} or WhatsApp, or email ${SITE.email}. Head office in Siliguri with a branch in Guwahati.`,
      links: [{ label: "Contact page", to: "/contact" }],
      wa: "Hi TripTrix, I'd like to get in touch with your trade desk.",
      call: true,
    },
  },
  {
    id: "payment",
    words: ["payment", "pay", "advance", "deposit", "refund", "cancellation", "cancel", "gst", "invoice"],
    reply: {
      text: "Usually an advance confirms services with the balance before travel; the exact terms (and any cancellation policy) come with your net-rate quote, since they depend on the hotels and services booked.",
      links: [{ label: "Send an enquiry", to: "/contact" }],
      wa: "Hi TripTrix, I have a question about payment terms:",
    },
  },
  {
    id: "about",
    words: ["about", "who is triptrix", "who are triptrix", "company", "established", "experience", "since", "history", "vedic"],
    reply: {
      text: `TripTrix Voyages is a North East India ground DMC for the travel trade, operating since 2016 (${SITE.parentNote}). We're GST-registered and Udyam/MSME, with offices in Siliguri and Guwahati.`,
      links: [{ label: "Our story", to: "/about" }],
    },
  },
  {
    id: "food",
    words: ["food", "foods", "foodie", "cuisine", "dish", "dishes", "restaurant", "restaurants", "cafe", "cafes", "momos", "momo", "thukpa", "street food", "local food", "what to eat", "where to eat", "ema datshi", "smoked pork", "bamboo shoot"],
    reply: {
      text: "Great choice — the region's food is a trip in itself: momos & thukpa, Assamese thalis, Naga smoked meats, Sikkimese and Bhutanese dishes, and Assam tea. We also weave food walks and local eateries into client itineraries.",
      links: [
        { label: "North East India Food Guide", to: "/journal/north-east-india-food-guide" },
        { label: "Momos & Thukpa guide", to: "/journal/himalayan-momos-thukpa-guide" },
        { label: "Assamese cuisine guide", to: "/journal/assam-cuisine-guide" },
        { label: "All food guides", to: "/journal" },
      ],
    },
  },
  {
    id: "destinations",
    words: ["destination", "destinations", "where", "meghalaya", "assam", "arunachal", "sikkim", "nagaland", "manipur", "mizoram", "tripura", "darjeeling", "bhutan", "nepal", "tawang", "kaziranga", "shillong", "gangtok", "cherrapunji", "dawki", "ziro", "majuli", "dzukou", "hornbill", "kohima"],
    reply: {
      text: "We operate right across the North East — Meghalaya, Assam, Arunachal, Sikkim, Nagaland, Manipur, Mizoram, Tripura and Darjeeling — plus Bhutan and Nepal. Open a destination for highlights, best months and sample routes.",
      links: [
        { label: "All destinations", to: "/destinations" },
        { label: "North East India DMC", to: "/northeast-dmc" },
      ],
    },
  },
  // ---- small talk ----
  {
    id: "greeting",
    match: (t) => /\b(hi|hii|hey|hiya|yo|hello|helo|hola|namaste|namaskar|good\s+(morning|afternoon|evening|day))\b/.test(t),
    reply: {
      text: "Hello! I'm Trixie, the TripTrix assistant. Ask me about destinations, permits, transport or net rates — or tell me your travel dates and I'll get our trade desk on it.",
    },
  },
  {
    id: "name",
    match: (t) => /\b(your name|who are you|what are you|who is this|what.?s your name|u name)\b/.test(t),
    reply: {
      text: "I'm Trixie, TripTrix Voyages' virtual assistant. I help travel agents and tour operators with destinations, permits, transport and net rates across the North East, Sikkim, Bhutan, Nepal and Darjeeling.",
    },
  },
  {
    id: "howareyou",
    match: (t) => /\b(how are you|how r u|how are u|hows it going|how.?s it going|how do you do|wassup|what.?s up|whats up)\b/.test(t),
    reply: {
      text: "Doing great, thanks for asking — and ready to help you plan! What destination or service are you looking at?",
    },
  },
  {
    id: "thanks",
    match: (t) => /\b(thanks|thank you|thankyou|thx|ty|appreciate|great help|nice)\b/.test(t),
    reply: {
      text: "You're most welcome! Anything else I can help with — packages, permits, transport or net rates?",
    },
  },
  {
    id: "bye",
    match: (t) => /\b(bye|goodbye|see you|see ya|cya|take care|later|good night)\b/.test(t),
    reply: {
      text: "Thanks for stopping by! When you're ready, send your dates and pax and we'll share net rates. Safe travels!",
    },
  },
  {
    id: "help",
    match: (t) => /\b(help|what can you do|what do you do|options|menu|assist|support)\b/.test(t),
    reply: {
      text: "I can help with tour packages, the best time to visit, transport & transfers, permits (ILP/PAP), how agents work with us, our offices and net rates. What would you like to know?",
    },
  },
];

// Returns a reply object: { text, links?, wa?, call?, openLead? }
export async function getBotAnswer(raw) {
  const t = norm(raw);
  for (const intent of INTENTS) {
    if (intent.match ? intent.match(t) : hasAny(t, intent.words)) {
      return { from: "bot", ...intent.reply };
    }
  }
  // Fallback: search the guide catalogue (lazy-loads the heavy guide index on
  // first use), else hand off to the trade desk + capture a lead.
  const guides = await getRelatedGuides(String(raw || "").split(/\s+/), 4);
  if (guides.length) {
    return {
      from: "bot",
      text: `Here's what I found for "${String(raw).trim()}":`,
      links: guides.map((g) => ({ label: g.title, to: `/journal/${g.slug}` })),
    };
  }
  return {
    from: "bot",
    text: "I'm not fully sure on that one — but our trade desk will know. Leave your number and we'll call you back, or reach us on WhatsApp.",
    wa: `Hi TripTrix, I have a question: ${String(raw).trim()}`,
    openLead: true,
  };
}
